/**
 * W76.5 — Binance Funding-Rates fuer Perpetuals.
 *
 * Audit Agent F + E HIGH: Funding-Extreme = Sentiment-Indikator.
 *   - Funding > +0.05 % / 8h = Long-Crowded, Short-Squeeze-Setup
 *   - Funding < -0.02 % / 8h = Short-Crowded, Long-Squeeze-Setup
 *
 * Binance Public-API: https://fapi.binance.com/fapi/v1/premiumIndex
 * Kein Auth, kein Rate-Limit-Issue fuer einzelne Calls.
 *
 * Mapping Spot-Symbol → Perpetual-Symbol:
 *   BTC/USD → BTCUSDT, ETH/USD → ETHUSDT, SOL/USD → SOLUSDT, etc.
 */

export interface FundingRateSnapshot {
  symbol: string;
  perpetual_symbol: string;
  /** lastFundingRate aus Binance (Bruchteil, z.B. 0.0001 = +0.01 %) */
  funding_rate: number;
  /** funding_rate × 100 fuer Anzeige (Prozent) */
  funding_rate_pct: number;
  /** Klartext-Bewertung DE */
  phase_de: string;
  /** Trade-Hinweis (Contrarian-Signal) */
  hint_de: string;
  /** Naechstes Funding-Time (ms) */
  next_funding_time: number;
  /** ISO-Timestamp */
  fetched_at: string;
}

const SYMBOL_TO_PERP: Record<string, string> = {
  'BTC/USD': 'BTCUSDT',
  'ETH/USD': 'ETHUSDT',
  'SOL/USD': 'SOLUSDT',
  'BNB/USD': 'BNBUSDT',
  'XRP/USD': 'XRPUSDT',
  'ADA/USD': 'ADAUSDT',
  'DOGE/USD': 'DOGEUSDT',
  'AVAX/USD': 'AVAXUSDT',
  'DOT/USD': 'DOTUSDT',
  'LINK/USD': 'LINKUSDT',
};

function classifyDe(rate: number): { phase: string; hint: string } {
  const pct = rate * 100;
  if (pct > 0.05) {
    return {
      phase: 'Long-Crowded',
      hint: 'Funding > +0.05% — Longs zahlen Shorts, Squeeze-Risiko fuer Long-Trades hoch',
    };
  }
  if (pct > 0.02) {
    return {
      phase: 'Bullish-Bias',
      hint: 'Funding positiv aber moderat — gesunder Bull-Markt-Indikator',
    };
  }
  if (pct >= -0.01) {
    return {
      phase: 'Neutral',
      hint: 'Funding nahe 0 — kein klarer Sentiment-Bias',
    };
  }
  if (pct >= -0.02) {
    return {
      phase: 'Bearish-Bias',
      hint: 'Funding negativ — Shorts zahlen Longs, vorsichtige Bear-Sentiment',
    };
  }
  return {
    phase: 'Short-Crowded',
    hint: 'Funding < -0.02% — Shorts ueberhitzt, Long-Squeeze-Setup wahrscheinlich',
  };
}

interface BinancePremiumIndex {
  symbol: string;
  lastFundingRate: string;
  nextFundingTime: number;
  time: number;
}

export async function fetchFundingRate(
  symbol: string,
  signal?: AbortSignal
): Promise<FundingRateSnapshot | null> {
  const perp = SYMBOL_TO_PERP[symbol.toUpperCase()];
  if (!perp) return null;
  try {
    const res = await fetch(
      `https://fapi.binance.com/fapi/v1/premiumIndex?symbol=${perp}`,
      { signal }
    );
    if (!res.ok) return null;
    const data = (await res.json()) as BinancePremiumIndex;
    const rate = Number(data.lastFundingRate);
    if (!Number.isFinite(rate)) return null;
    const { phase, hint } = classifyDe(rate);
    return {
      symbol,
      perpetual_symbol: perp,
      funding_rate: rate,
      funding_rate_pct: rate * 100,
      phase_de: phase,
      hint_de: hint,
      next_funding_time: data.nextFundingTime,
      fetched_at: new Date(data.time).toISOString(),
    };
  } catch {
    return null;
  }
}
